/**
 * Shared guest-token manager.
 *
 * All API clients (public-client, custom-client, search-client, order-bf-client)
 * should call {@link ensureGuestToken} before making requests. This module
 * guarantees that:
 *
 * 1. A valid (non-expired) guest token exists in localStorage before the
 *    caller proceeds.
 * 2. If the current token is expired, a **new** guest token is fetched
 *    silently - no redirect to /login for anonymous users.
 * 3. If the current token belongs to an **authenticated** user and has
 *    expired, the user is redirected to /login (as before).
 * 4. Concurrent callers share a single in-flight token fetch (deduplication
 *    via a shared Promise) to avoid race conditions when multiple React Query
 *    hooks fire simultaneously.
 */

import { setAuthToken, getAuthToken, removeAuthToken } from '@/lib/utils';
import { publicEndpoints } from '@/lib/endpoints/public';
import type { AuthTokenResponse } from '@/lib/types/authentication';
import { isTokenExpired, decodeRoles, hasGuestRole } from '@/lib/types/jwt';
import { store } from '@/lib/store/store';
import { loginExpired } from '@/lib/store/auth/authSlice';
import { getImpersonationSession, clearImpersonationSession } from '@/lib/impersonation/session';

/**
 * Lazily imports `loginApi` to break a circular dependency:
 *   client.ts → token-manager.ts → login.ts → client.ts
 * `login.ts` calls `new ApiClient()` at module top level, so importing it
 * eagerly here would cause `ApiClient` to be undefined when `login.ts`
 * evaluates. By deferring the import to call time, the cycle is broken.
 */
const getLoginApi = async () => {
  const { loginApi } = await import('@/lib/api/login');
  return loginApi;
};

/**
 * Shared in-flight guest-token fetch promise. When multiple API clients call
 * `ensureGuestToken` concurrently (e.g. several React Query hooks firing on
 * page load), they all await the same Promise instead of each triggering a
 * separate `Authenticate/GetToken` request.
 */
let tokenFetchPromise: Promise<void> | null = null;

/**
 * Fetches a new guest token from the backend and stores it in localStorage.
 * Does **not** check whether a token already exists - callers should use
 * {@link ensureGuestToken} instead.
 */
const fetchGuestToken = async (): Promise<void> => {
  const loginApi = await getLoginApi();
  const response = await loginApi.post<AuthTokenResponse>(publicEndpoints.auth.login, {
    is_guest: true,
    username: null,
    email: null,
    password: null,
  });
  if (!response.data) return;
  const token = response.data.token;
  if (token && !isTokenExpired(token)) {
    setAuthToken(token);
  }
};

/**
 * Ensures that a valid (non-expired) token exists in localStorage before the
 * caller proceeds with an API request.
 *
 * Behaviour:
 * - **No token** → fetches a new guest token silently.
 * - **Expired guest token** → removes it and fetches a new guest token silently.
 * - **Expired authenticated-user token** → dispatches `loginExpired` and
 *   redirects to `/login` (the user must re-authenticate).
 * - **Valid token** → returns immediately.
 *
 * Concurrent calls are deduplicated: if a token fetch is already in progress,
 * all callers await the same Promise.
 */
export const ensureGuestToken = async (): Promise<void> => {
  // Skip token logic during server-side rendering / static generation
  if (typeof window === 'undefined') return;

  const authToken = getAuthToken();

  // No token at all → fetch a guest token
  if (!authToken) {
    if (!tokenFetchPromise) {
      tokenFetchPromise = fetchGuestToken().finally(() => {
        tokenFetchPromise = null;
      });
    }
    await tokenFetchPromise;
    return;
  }

  // Token exists - check expiry (isTokenExpired already includes a 30s
  // clock-skew buffer via jwtDecode, which handles base64url correctly)
  if (isTokenExpired(authToken)) {
    // Determine whether this was an authenticated user or a guest by
    // inspecting role claims directly. We cannot use isUser()/isGuest()
    // because those helpers return false for expired tokens, causing
    // expired guest tokens to be misidentified as user tokens.
    const roles = decodeRoles(authToken);
    if (roles.length > 0 && !hasGuestRole(roles)) {
      // Impersonation session: never redirect to /login. Clear the expired
      // impersonation token; the ImpersonationBanner shows the expired state
      // and offers "Return to admin". Redirecting would trap the admin in
      // the shop's login page mid-impersonation.
      if (getImpersonationSession()) {
        // Clear the whole session, not just the token: leaving `userType` in
        // localStorage would keep the SPA believing the visitor is signed in
        // as the impersonated customer after the token is gone.
        clearImpersonationSession();
        store.dispatch(loginExpired());
        return;
      }
      // Authenticated user's token expired - they need to log in again
      store.dispatch(loginExpired());
      window.location.href = '/login';
      return;
    }

    // Guest token expired - silently refresh
    removeAuthToken();
    if (!tokenFetchPromise) {
      tokenFetchPromise = fetchGuestToken().finally(() => {
        tokenFetchPromise = null;
      });
    }
    await tokenFetchPromise;
    return;
  }

  // Token is still valid - nothing to do
};

/**
 * Clears any in-flight token fetch state. Primarily useful for tests.
 */
export const resetTokenManager = (): void => {
  tokenFetchPromise = null;
};